/**
 * Build the 5-color `ScratchCardBrand` the vendored game canvases expect from
 * the 2-color runtime brand the public wizard hands every game
 * (`primary` + `accent`) plus the optional brand logo.
 *
 * Mirrors the intent of the vendored `resolveBrandFromSurveyObject` (keep the
 * structural surface distinct from the board background so a dark brand never
 * vanishes), but sources colours from the app's `BrandKit`-derived
 * `primary`/`accent` rather than the legacy 3-color `company` shape. Colour
 * math reuses the shared `_shared/colors` helpers — no new ad-hoc utilities.
 */
import type { ScratchCardBrand } from '../_vendor/ScratchCardCanvas';
import { shade, readableText, mix } from './colors';

export interface ResolveBrandInput {
  primary: string;
  accent: string;
  logo?: string | null;
}

export function resolveBrand({ primary, accent, logo }: ResolveBrandInput): ScratchCardBrand {
  // Board background: a deep, slightly accent-tinted derivation of primary so
  // the brand reads as one premium surface. Always darker than the foil.
  const background = mix(shade(primary, 0.16), accent, 0.06);
  return {
    primary, // structural foil / spinning surface — the brand itself
    secondary: accent, // edges / borders
    accent, // CTA, confetti, progress highlight
    background,
    font: readableText(primary),
    logo: logo ?? undefined,
  };
}
